/**
 * Builds checkout API payment fields for saved cards vs newly tokenized cards.
 * Saved cards and new Collect.js payment tokens both use `paymentMethodId`.
 */

export type LagoPlanPriceSource = {
  lagoId?: string | null;
  stripePriceId?: string | null;
  strip_priceId?: string | null;
  braintreePlanId?: string | null;
};

export type CheckoutPaymentApiFields = {
  paymentMethodId?: string;
  paymentMethodType?: number;
};

export type CheckoutBillingAddress = {
  streetAddress?: string;
  extendedAddress?: string;
  locality?: string;
  region?: string;
  postalCode?: string;
};

type CheckoutBillingFormSlice = {
  streetAddress?: string;
  apt?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  nameOnCredit?: string;
  firstName?: string;
  lastName?: string;
};

/** Shared checkout payment fields for reactivate / make-payment / upgrade APIs. */
export type CheckoutPaymentSubmitFields = CheckoutPaymentApiFields & {
  subscriptionId?: string;
};

export type HomePhoneUpdatePlanCheckoutPayload = CheckoutPaymentApiFields & {
  customerId: string;
  currentPlanId: string;
  newPlanId: string;
  zipCode: string;
  subscriptionId?: string;
};

/**
 * Only send non-empty address fields; some gateways treat empty strings as invalid.
 */
export function buildCheckoutBillingAddressFromForm(
  data: CheckoutBillingFormSlice,
): CheckoutBillingAddress {
  const billingAddress: CheckoutBillingAddress = {};
  const streetAddress = String(data.streetAddress || "").trim();
  const extendedAddress = String(data.apt || "").trim();
  const locality = String(data.city || "").trim();
  const region = String(data.state || "").trim();
  const postalCode = String(data.zipCode || "").trim();

  if (streetAddress) billingAddress.streetAddress = streetAddress;
  if (extendedAddress) billingAddress.extendedAddress = extendedAddress;
  if (locality) billingAddress.locality = locality;
  if (region) billingAddress.region = region;
  if (postalCode) billingAddress.postalCode = postalCode;

  return billingAddress;
}

export function resolveCheckoutCardholderName(
  data: CheckoutBillingFormSlice,
): string {
  return (
    data.nameOnCredit || `${data.firstName || ""} ${data.lastName || ""}`
  ).trim();
}

type CheckoutNewCardFormSlice = CheckoutBillingFormSlice & {
  selectedPaymentMethod?: string;
  selectedPaymentMethodType?: string;
};

function isCheckoutSavedCardFlow(
  selectedPaymentMethodType?: string,
  selectedPaymentMethod?: string,
): boolean {
  return (
    selectedPaymentMethodType === "savedCard" ||
    selectedPaymentMethodType === "saved" ||
    selectedPaymentMethod === "saved"
  );
}

/**
 * Cardholder name for POST /api/card only when persisting a newly tokenized card.
 * Omit for saved-card checkout, make-default, and payment/charge APIs.
 */
export function resolveNewCardSaveName(
  form: CheckoutNewCardFormSlice,
  selectedPaymentMethodType?: string,
): string | undefined {
  if (
    isCheckoutSavedCardFlow(
      selectedPaymentMethodType,
      form.selectedPaymentMethod,
    )
  ) {
    return undefined;
  }
  const isAddCard =
    selectedPaymentMethodType === "addCard" ||
    form.selectedPaymentMethod === "addCard" ||
    form.selectedPaymentMethodType === "addCard";
  if (!isAddCard) return undefined;
  const name = resolveCheckoutCardholderName(form);
  return name || undefined;
}

/** Lago create-subscription expects `plans[].priceId` to be the plan `lagoId`. */
export function resolveLagoCheckoutPlanPriceId(
  plan: LagoPlanPriceSource | null | undefined,
): string | undefined {
  // Prefer lagoId; fall back to legacy stripePriceId / braintreePlanId until catalog is fully migrated.
  for (const candidate of [
    plan?.lagoId,
    plan?.stripePriceId,
    plan?.strip_priceId,
    plan?.braintreePlanId,
  ]) {
    const trimmed = typeof candidate === "string" ? candidate.trim() : "";
    if (trimmed) return trimmed;
  }
  return undefined;
}

export function resolveLagoCheckoutPlanPriceIdFromSources(
  ...sources: Array<LagoPlanPriceSource | string | null | undefined>
): string | undefined {
  for (const source of sources) {
    if (typeof source === "string") {
      const trimmed = source.trim();
      if (trimmed) return trimmed;
      continue;
    }
    const resolved = resolveLagoCheckoutPlanPriceId(source);
    if (resolved) return resolved;
  }
  return undefined;
}

export function buildCheckoutPlansWithLagoPriceIds(
  priceIds: Array<string | undefined>,
): Array<{ priceId: string; quantity: number }> {
  return priceIds
    .map((priceId) => (typeof priceId === "string" ? priceId.trim() : ""))
    .filter(Boolean)
    .map((priceId) => ({ priceId, quantity: 1 }));
}

export function buildCheckoutPaymentPayload(
  paymentTokenOrSavedId: string,
  _selectedPaymentMethodType?: string,
  paymentMethodType?: number,
): CheckoutPaymentApiFields {
  const base =
    paymentMethodType !== undefined ? { paymentMethodType } : {};
  // Saved cards and newly tokenized Collect.js tokens both map to paymentMethodId.
  return { ...base, paymentMethodId: paymentTokenOrSavedId };
}

/**
 * Lago checkout (home-phone, mobile create-subscription, etc.) sends the payment
 * token (Collect.js payment_token) or a saved card id under `paymentMethodId` — there is no
 * separate legacy token field on migrated endpoints.
 */
export function buildLagoCheckoutPaymentPayload(
  paymentTokenOrSavedId: string,
  paymentMethodType?: number,
): CheckoutPaymentApiFields {
  return buildCheckoutPaymentPayload(
    paymentTokenOrSavedId,
    undefined,
    paymentMethodType,
  );
}

/** Maps checkout payment fields onto API request bodies (Collect.js token vs saved card). */
export function checkoutPaymentFieldsToApiBody(
  payment: CheckoutPaymentApiFields,
): CheckoutPaymentApiFields {
  const body: CheckoutPaymentApiFields = {};
  if (payment.paymentMethodId) {
    body.paymentMethodId = payment.paymentMethodId;
  }
  if (payment.paymentMethodType !== undefined) {
    body.paymentMethodType = payment.paymentMethodType;
  }
  return body;
}

export type CreateCardPayload = {
  customerId: string;
  paymentMethodId: string;
  isDefault: boolean;
  nameOnCard?: string;
};

export type CheckoutCreateCardApiRef = {
  customerId?: string;
  paymentMethodId: string;
  nameOnCard?: string;
};

/** Stashes customer/payment ids (and optional cardholder name) for post-checkout createCard. */
export function buildCheckoutCreateCardApiRef(
  paymentMethodId: string,
  form: CheckoutBillingFormSlice,
  customerId?: string,
): CheckoutCreateCardApiRef {
  const ref: CheckoutCreateCardApiRef = { paymentMethodId };
  const resolvedCustomerId = customerId?.trim();
  if (resolvedCustomerId) ref.customerId = resolvedCustomerId;
  const nameOnCard = resolveNewCardSaveName(form, "addCard");
  if (nameOnCard) ref.nameOnCard = nameOnCard;
  return ref;
}

/** POST /api/card body. Pass nameOnCard only when saving a newly tokenized card. */
export function buildCreateCardPayload(
  customerId: string,
  paymentMethodId: string,
  isDefault: boolean,
  nameOnCard?: string,
): CreateCardPayload {
  const payload: CreateCardPayload = {
    customerId,
    paymentMethodId,
    isDefault,
  };
  const resolvedName = nameOnCard?.trim();
  if (resolvedName) payload.nameOnCard = resolvedName;
  return payload;
}
